import { useEffect, useState } from "react";
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  X,
  ArrowLeft,
  Banknote,
  CheckCircle2,
  Copy,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { useCart } from "../context/CartContext";

const emptyForm = {
  fullName: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  county: "",
  postalCode: "",
  notes: "",
};

// Note: form data is intentionally NOT restored from localStorage anymore.
// Customers asked for a clean blank form on every visit — they want to type
// everything themselves, no auto-fill of any kind.

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    initialView,
    closeCart,
    updateQty,
    removeItem,
    totalPrice,
    clearCart,
  } = useCart();

  const [view, setView] = useState("cart"); // "cart" | "checkout" | "success"
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  // When drawer opens, respect the requested initial view (cart vs. checkout)
  // AND wipe any previously persisted checkout data so the form is always blank.
  useEffect(() => {
    if (isOpen) {
      setView(initialView || "cart");
      try {
        localStorage.removeItem("dd_checkout_v1");
      } catch {
        // ignore
      }
    }
  }, [isOpen, initialView]);

  const handleField = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 3)
      errs.fullName = "Introdu numele complet (min. 3 caractere)";
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 9)
      errs.phone = "Introdu un număr de telefon valid";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Email invalid";
    if (!form.address.trim() || form.address.trim().length < 5)
      errs.address = "Introdu adresa completă (stradă, număr, bloc, ap.)";
    if (!form.city.trim()) errs.city = "Introdu localitatea";
    if (!form.county.trim()) errs.county = "Introdu județul";
    // Postal code: optional, but if provided must be 6 digits
    if (form.postalCode.trim() && form.postalCode.replace(/\D/g, "").length !== 6)
      errs.postalCode = "Codul poștal trebuie să aibă 6 cifre";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goToCheckout = () => {
    if (items.length === 0) return;
    setView("checkout");
  };

  const handleConfirm = async () => {
    if (!validate()) {
      toast.error("Completează corect câmpurile obligatorii");
      return;
    }

    // Address validation is informational only — we NEVER block submission.
    // If Cargus can't generate the AWB later, the admin gets an alert.

    setSubmitting(true);

    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      // Abort the request if it takes longer than 60s so the button never stays
      // stuck on „Se trimite..." forever.
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 60000);
      let response;
      try {
        response = await fetch(`${apiBase}/api/orders`, {
          method: "POST",
          signal: ctrl.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: items.map((it) => ({
              id: it.id,
              title: it.title,
              price: it.price,
              qty: it.qty,
              slug: it.slug,
            })),
            total: totalPrice,
            customer: {
              fullName: form.fullName.trim(),
              phone: form.phone.trim(),
              email: form.email.trim() || null,
              address: form.address.trim(),
              city: form.city.trim(),
              county: form.county.trim(),
              postalCode: form.postalCode.trim() || null,
              notes: form.notes.trim() || null,
            },
          }),
        });
      } finally {
        clearTimeout(tid);
      }

      if (!response.ok) {
        let detail = "Eroare la procesarea comenzii";
        let errBody = null;
        try {
          errBody = await response.json();
          detail = errBody.detail || detail;
        } catch {
          // ignore
        }
        // Special handling: address mismatch — show inline errors + suggestion
        if (
          response.status === 400 &&
          typeof detail === "object" &&
          detail?.code === "ADDRESS_MISMATCH"
        ) {
          const msg = detail.message || "";
          const newErrors = {};
          if (msg.includes("Județul")) {
            newErrors.county = `Județ greșit${detail.matched_county ? ` (corect: ${detail.matched_county})` : ""}`;
          } else if (msg.includes("Localitatea")) {
            newErrors.city = `Localitate greșită${detail.matched_city ? ` (corect: ${detail.matched_city})` : ""}`;
          } else {
            newErrors.address = msg || "Adresă invalidă";
          }
          setErrors((er) => ({ ...er, ...newErrors }));
          const sugg = detail.suggestion ? ` ${detail.suggestion}` : "";
          toast.error(msg || "Adresă invalidă", {
            description: sugg.trim() || "Corectează datele și încearcă din nou.",
            duration: 9000,
          });
          return;
        }
        throw new Error(typeof detail === "string" ? detail : detail?.message || "Eroare");
      }

      const data = await response.json();
      // Meta Pixel - Purchase event
      try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: Number(totalPrice) || 0,
            currency: "RON",
            content_ids: items.map((it) => String(it.id)),
            content_type: "product",
            contents: items.map((it) => ({
              id: String(it.id),
              quantity: Number(it.qty) || 1,
              item_price: Number(it.price) || 0,
            })),
            num_items: items.reduce((n, it) => n + (Number(it.qty) || 0), 0),
            order_id: data.order_number,
          });
        }
      } catch (e) {
        // ignore pixel errors
      }
      setConfirmedOrder(data);
      setView("success");
      clearCart();
      toast.success(`Comanda #${data.order_number} confirmată!`);
    } catch (err) {
      const msg =
        err?.name === "AbortError"
          ? "Comanda a durat prea mult. Te rugăm să încerci din nou — dacă problema persistă, sună-ne."
          : err?.message || "Te rugăm să încerci din nou.";
      toast.error("Nu s-a putut plasa comanda", {
        description: msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSheetChange = (open) => {
    if (!open) {
      closeCart();
      setTimeout(() => {
        setView("cart");
        setConfirmedOrder(null);
      }, 200);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleSheetChange}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md flex flex-col p-0 bg-white"
      >
        <SheetHeader className="px-6 py-5 border-b border-sky-100">
          <SheetTitle className="font-heading text-xl font-black text-slate-900 flex items-center gap-2.5">
            {view === "success" ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Comandă plasată
              </>
            ) : view === "checkout" ? (
              <>
                <button
                  onClick={() => setView("cart")}
                  data-testid="checkout-back"
                  className="p-1 -ml-1 rounded-lg hover:bg-sky-50 text-slate-700"
                  aria-label="Înapoi la coș"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                Finalizare comandă
              </>
            ) : (
              <>
                <ShoppingBag className="w-5 h-5 text-cyan-600" />
                Coșul tău
                {items.length > 0 && (
                  <span className="text-sm font-semibold text-slate-500">
                    ({items.length} {items.length === 1 ? "produs" : "produse"})
                  </span>
                )}
              </>
            )}
          </SheetTitle>
        </SheetHeader>

        {view === "success" && confirmedOrder ? (
          <SuccessView
            order={confirmedOrder}
            onClose={() => {
              closeCart();
              setTimeout(() => {
                setView("cart");
                setConfirmedOrder(null);
              }, 200);
            }}
          />
        ) : items.length === 0 && view === "cart" ? (
          <div
            data-testid="cart-empty-state"
            className="flex-1 flex flex-col items-center justify-center px-6 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-sky-50 flex items-center justify-center mb-5">
              <ShoppingBag className="w-9 h-9 text-cyan-600" />
            </div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
              Coșul tău este gol
            </h3>
            <p className="text-sm text-slate-500 mb-6 max-w-xs">
              Adaugă produsele dorite pentru a comanda rapid cu plată la livrare.
            </p>
            <button
              onClick={closeCart}
              data-testid="cart-continue-shopping"
              className="h-11 px-6 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-sm transition-colors"
            >
              Vezi produsele
            </button>
          </div>
        ) : view === "cart" ? (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`cart-item-${it.id}`}
                  className="flex gap-3 pb-4 border-b border-sky-50 last:border-0"
                >
                  <Link
                    to={`/produs/${it.slug}`}
                    onClick={closeCart}
                    className="shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-white border border-sky-100"
                  >
                    <img
                      src={it.image}
                      alt={it.title}
                      className="w-full h-full object-contain"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/produs/${it.slug}`}
                      onClick={closeCart}
                      className="block font-heading font-bold text-sm text-slate-900 hover:text-cyan-600 line-clamp-2"
                    >
                      {it.title}
                    </Link>
                    <div className="text-sm font-black text-cyan-600 mt-1">
                      {it.price} lei
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-full border border-slate-200 bg-white">
                        <button
                          onClick={() => updateQty(it.id, it.qty - 1)}
                          data-testid={`cart-decrease-${it.id}`}
                          className="w-8 h-8 flex items-center justify-center text-slate-700 hover:text-cyan-600"
                          aria-label="Scade cantitatea"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span
                          data-testid={`cart-qty-${it.id}`}
                          className="w-7 text-center text-sm font-bold"
                        >
                          {it.qty}
                        </span>
                        <button
                          onClick={() => updateQty(it.id, it.qty + 1)}
                          data-testid={`cart-increase-${it.id}`}
                          className="w-8 h-8 flex items-center justify-center text-slate-700 hover:text-cyan-600"
                          aria-label="Crește cantitatea"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        data-testid={`cart-remove-${it.id}`}
                        className="text-slate-400 hover:text-rose-500 transition-colors"
                        aria-label="Șterge produs"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-sky-100 px-6 py-5 bg-sky-50/50">
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
                  Total
                </span>
                <span
                  data-testid="cart-total"
                  className="font-heading text-2xl font-black text-slate-900"
                >
                  {totalPrice} lei
                </span>
              </div>
              <button
                onClick={goToCheckout}
                data-testid="cart-checkout-whatsapp"
                className="w-full h-13 py-3.5 rounded-xl bg-[#25d366] hover:bg-[#1da851] text-white font-bold text-base flex items-center justify-center gap-2.5 transition-colors shadow-lg shadow-emerald-200"
              >
                <Banknote className="w-5 h-5" />
                Comandă cu Plată la Livrare
              </button>
              <button
                onClick={clearCart}
                data-testid="cart-clear"
                className="w-full mt-2 h-10 rounded-xl text-slate-500 hover:text-rose-500 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Golește coșul
              </button>
            </div>
          </>
        ) : (
          // CHECKOUT VIEW
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3.5 flex items-start gap-2.5">
                <Banknote className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-xs text-emerald-900 leading-relaxed">
                  <strong>Plată numerar la curier (ramburs).</strong> Completează
                  datele tale, confirmi comanda și te contactăm rapid pentru
                  livrare.
                </div>
              </div>

              <FormField
                label="Nume și prenume"
                required
                value={form.fullName}
                onChange={handleField("fullName")}
                placeholder="ex: Ion Popescu"
                error={errors.fullName}
                testid="checkout-fullname"
              />

              <FormField
                label="Telefon"
                required
                type="tel"
                value={form.phone}
                onChange={handleField("phone")}
                placeholder="ex: 0712 345 678"
                error={errors.phone}
                testid="checkout-phone"
              />

              <FormField
                label="Email (opțional)"
                type="email"
                value={form.email}
                onChange={handleField("email")}
                placeholder="ex: ion@email.ro"
                error={errors.email}
                testid="checkout-email"
              />

              <FormField
                label="Adresă completă"
                required
                value={form.address}
                onChange={handleField("address")}
                placeholder="Stradă, număr, bloc, scară, ap."
                error={errors.address}
                testid="checkout-address"
              />

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  label="Localitate"
                  required
                  value={form.city}
                  onChange={handleField("city")}
                  placeholder="ex: București"
                  error={errors.city}
                  testid="checkout-city"
                />
                <FormField
                  label="Județ"
                  required
                  value={form.county}
                  onChange={handleField("county")}
                  placeholder="ex: Ilfov"
                  error={errors.county}
                  testid="checkout-county"
                />
              </div>

              <FormField
                label="Cod poștal (opțional)"
                value={form.postalCode}
                onChange={handleField("postalCode")}
                placeholder="ex: 010101"
                error={errors.postalCode}
                testid="checkout-postal"
              />

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Mențiuni (opțional)
                </label>
                <textarea
                  value={form.notes}
                  onChange={handleField("notes")}
                  rows={2}
                  placeholder="Interfon, ore livrare etc."
                  data-testid="checkout-notes"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none text-sm resize-none"
                />
              </div>

              {/* Order summary */}
              <div className="mt-2 rounded-xl bg-sky-50 border border-sky-100 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Sumar comandă
                </div>
                <ul className="space-y-1 text-sm text-slate-700">
                  {items.map((it) => (
                    <li key={it.id} className="flex justify-between gap-3">
                      <span className="line-clamp-1">
                        {it.qty}× {it.title}
                      </span>
                      <span className="font-bold whitespace-nowrap">
                        {(it.qty * it.price).toFixed(2)} lei
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 pt-3 border-t border-sky-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-900">
                    Total de plată
                  </span>
                  <span className="font-heading text-xl font-black text-cyan-600">
                    {totalPrice} lei
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-sky-100 px-6 py-4 bg-white">
              <button
                onClick={handleConfirm}
                disabled={submitting}
                data-testid="checkout-confirm"
                className="w-full h-13 py-3.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-bold text-base flex items-center justify-center gap-2.5 transition-colors shadow-lg shadow-red-200"
              >
                <CheckCircle2 className="w-5 h-5" />
                {submitting ? "Se trimite..." : "Confirmă comanda"}
              </button>
              <p className="text-[11px] text-center text-slate-500 mt-2">
                Apăsând „Confirmă comanda" accepți{" "}
                <Link
                  to="/termeni-conditii"
                  onClick={closeCart}
                  className="text-cyan-600 underline"
                >
                  termenii
                </Link>{" "}
                și{" "}
                <Link
                  to="/politica-confidentialitate"
                  onClick={closeCart}
                  className="text-cyan-600 underline"
                >
                  politica de confidențialitate
                </Link>
                .
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const FormField = ({
  label,
  required,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  testid,
}) => (
  <div>
    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
      {label}
      {required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      name={testid /* unique non-standard name disables Chrome autofill */}
      className={`w-full h-11 px-3.5 rounded-xl border outline-none text-sm transition-colors ${
        error
          ? "border-rose-400 focus:border-rose-500 bg-rose-50/40"
          : "border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
      }`}
    />
    {error && (
      <p className="mt-1 text-[11px] text-rose-600 font-medium">{error}</p>
    )}
  </div>
);

const SuccessView = ({ order, onClose }) => {
  const handleCopy = () => {
    navigator.clipboard?.writeText(order.order_number);
    toast.success("Numărul comenzii a fost copiat");
  };

  return (
    <div
      data-testid="checkout-success"
      className="flex-1 flex flex-col overflow-y-auto"
    >
      <div className="px-6 py-8 text-center bg-gradient-to-b from-emerald-50 to-white border-b border-emerald-100">
        <div className="w-20 h-20 rounded-full bg-white border-4 border-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <h2 className="font-heading text-2xl font-black text-slate-900">
          Mulțumim pentru comandă!
        </h2>
        <p className="text-sm text-slate-600 mt-2 max-w-xs mx-auto leading-relaxed">
          Comanda ta a fost înregistrată cu succes. Te vom suna în scurt timp
          pentru a confirma livrarea.
        </p>
      </div>

      <div className="px-6 py-6 space-y-4">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">
            Numărul comenzii
          </div>
          <div className="mt-1 flex items-center justify-between gap-3">
            <span
              data-testid="success-order-number"
              className="font-heading text-lg md:text-xl font-black text-slate-900 tracking-wide"
            >
              {order.order_number}
            </span>
            <button
              onClick={handleCopy}
              className="text-xs font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              Copiază
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-3">
            <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-bold">
              Total
            </div>
            <div className="font-heading text-lg font-black text-slate-900 mt-0.5">
              {order.total} lei
            </div>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
              Plată
            </div>
            <div className="font-heading text-sm font-black text-slate-900 mt-0.5">
              Ramburs la curier
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-sky-50 border border-sky-100 p-4">
          <h3 className="font-heading font-bold text-sm text-slate-900 mb-2">
            Ce urmează?
          </h3>
          <ol className="space-y-2 text-xs text-slate-700">
            <li className="flex gap-2">
              <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center">
                1
              </span>
              Te contactăm telefonic pentru confirmarea comenzii
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center">
                2
              </span>
              Pregătim și expediem coletul în 24h
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center">
                3
              </span>
              Primești coletul în 24-48h și plătești cash la curier
            </li>
          </ol>
        </div>

        <div
          data-testid="success-terms-confirmation"
          className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4"
        >
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed">
              Confirm comanda și accept termenii și condițiile. Totodată, îmi
              asum responsabilitatea de a ridica coletul și de a achita
              contravaloarea acestuia la curier, în momentul livrării.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-auto border-t border-sky-100 px-6 py-4 bg-white">
        <button
          onClick={onClose}
          data-testid="success-close"
          className="w-full h-12 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-bold transition-colors"
        >
          Continuă cumpărăturile
        </button>
      </div>
    </div>
  );
};

